import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import { getWorkflowStatus, startProductSync } from '../services/catalog-import.service';
import type {
  CatalogImportPhase,
  CatalogImportStatus,
  CatalogImportView,
  TriggerSyncResponse,
  WorkflowStatusResponse,
} from '../types/catalog-import.types';
import type { ConnectionType } from '../types/connection.types';

/** Kick off a catalog sync workflow for a connection (GMC, Meta Commerce
 *  Manager, …). The screen persists the returned `workflowId` in the URL so
 *  a hard-refresh on Step 3 still finds the right workflow to poll. */
export function useStartCatalogImport() {
  const { t } = useTranslation('onboarding');
  return useMutation({
    mutationFn: ({
      connectionId,
      connectorCode,
    }: {
      connectionId: string;
      // Narrowed to ConnectionType (was `string`) so the M1 tightening on
      // `ConnectedConnectorResponse.code` propagates through every call site.
      connectorCode: ConnectionType;
    }) => startProductSync({ connection_id: connectionId, connector_code: connectorCode }),
    onError: (err) => {
      // Surface the backend message when available; fall back to the
      // translated default. Was a hardcoded English literal before — every
      // other mutation in the file pipes through `extractApiError`.
      toast.error(extractApiError(err, t('catalogImport.toasts.startError')));
    },
  });
}

/** Kick off a **scoped** catalog import — the same `/products/sync` trigger with
 *  a non-empty `productIds` list (≤ 50 verbatim opaque ids) plus an optional
 *  analytics window (`since`/`until`). Supported for the `SCOPED_IMPORT_SOURCES`
 *  connectors (GMC + Meta Commerce); the caller gates on connector.
 *  On success returns the products-import `TriggerSyncResponse` (the screen
 *  navigates to Step 3 with `data.workflowId`, exactly like the full import). */
export function useStartScopedCatalogImport() {
  const { t } = useTranslation('onboarding');
  return useMutation<
    TriggerSyncResponse,
    unknown,
    {
      connectionId: string;
      connectorCode: ConnectionType;
      productIds: string[];
      since?: string;
      until?: string;
    }
  >({
    mutationFn: ({ connectionId, connectorCode, productIds, since, until }) =>
      startProductSync({
        connection_id: connectionId,
        connector_code: connectorCode,
        productIds,
        ...(since ? { since } : {}),
        ...(until ? { until } : {}),
      }),
    onError: (err) => {
      // The two scoped-mode guards return a structured `detail.error_code`
      // (surfaced as `ApiError.serverCode`); a running sync returns 409. Both
      // guards are prevented client-side (≤50 cap, connector gating) — handled
      // here defensively. Everything else falls back to the server message.
      const serverCode = err instanceof ApiError ? err.serverCode : undefined;
      const status = err instanceof ApiError ? err.statusCode : undefined;
      if (serverCode === 'PRODUCT_CAP_EXCEEDED') {
        toast.error(t('scopedImport.toasts.capExceeded'));
        return;
      }
      if (serverCode === 'SCOPED_IMPORT_UNSUPPORTED_CONNECTOR') {
        toast.error(t('scopedImport.toasts.unsupportedConnector'));
        return;
      }
      if (status === 409) {
        toast.error(t('scopedImport.toasts.conflict'));
        return;
      }
      toast.error(extractApiError(err, t('scopedImport.toasts.startError')));
    },
  });
}

/** Poll workflow status every 5s while RUNNING/PENDING/PAUSED; stops when the
 *  workflow reaches a terminal state (COMPLETED / FAILED / CANCELLED). The
 *  `select` adapter flattens the Temporal progress shape into the tri-state
 *  view the UI actually renders. `source` is the human-readable platform
 *  label shown in the progress copy — caller-controlled (required) because
 *  the workflow status payload doesn't carry it back. */
export function useWorkflowImportStatus(workflowId: string | undefined, source: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.workflows.status(workflowId ?? ''),
    queryFn: async () => {
      const data = await getWorkflowStatus(workflowId ?? '');
      // Invalidate the `connected` list once the import terminates so any
      // downstream screen re-reads the connection's freshly-synced state.
      if (isTerminal(data.status)) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.connections.connected() });
      }
      return data;
    },
    enabled: !!workflowId,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s && !isTerminalWireStatus(s) ? 5000 : false;
    },
    select: (data) => mapWorkflowToImportView(data, source),
  });
}

// ---------------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------------

function isTerminal(s: WorkflowStatusResponse['status']): boolean {
  return s === 'COMPLETED' || s === 'FAILED' || s === 'CANCELLED';
}
function isTerminalWireStatus(s: WorkflowStatusResponse['status']): boolean {
  return isTerminal(s);
}

function mapWireStatus(s: WorkflowStatusResponse['status']): CatalogImportStatus {
  if (s === 'COMPLETED') return 'completed';
  if (s === 'FAILED' || s === 'CANCELLED') return 'failed';
  return 'in_progress';
}

/** Wire status → the five-way phase the run-controls card renders. PENDING is
 *  "queued to run", which the user experiences as running. */
export function mapWirePhase(s: WorkflowStatusResponse['status']): CatalogImportPhase {
  switch (s) {
    case 'COMPLETED':
      return 'completed';
    case 'FAILED':
      return 'failed';
    case 'CANCELLED':
      return 'cancelled';
    case 'PAUSED':
      return 'paused';
    default:
      return 'running';
  }
}

/** Seconds → largest-unit-first short form: "2 Hr 7 Min" / "3 Min 12s" / "39s".
 *  Large imports run for hours, so minutes roll into hours (a flat "127 Min 36s"
 *  was the bug). At ≥1 Hr seconds are dropped — noise at that scale, and
 *  "2 Hr 36s" would read ambiguously. */
export function formatElapsed(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return minutes > 0 ? `${String(hours)} Hr ${String(minutes)} Min` : `${String(hours)} Hr`;
  }
  if (minutes > 0) {
    return secs > 0 ? `${String(minutes)} Min ${String(secs)}s` : `${String(minutes)} Min`;
  }
  return `${String(secs)}s`;
}

/** Backend Temporal status/progress → the flat view the screen renders.
 *  Field-by-field mapping lives here so the screen stays presentational.
 *
 *  On COMPLETED the wire payload swaps `progress` (live polling shape) for
 *  `result` + `stages` (terminal shape). We prefer `result.total_synced` /
 *  `result.ch_rows_written` for the totals and derive elapsed time from
 *  `stages[last].completedAt - stages[0].startedAt` per backend contract. */
export function mapWorkflowToImportView(
  data: WorkflowStatusResponse,
  source: string,
): CatalogImportView {
  const progress = data.progress ?? null;
  const result = data.result ?? null;
  const isCompleted = data.status === 'COMPLETED';

  // Prefer the terminal `result` payload on COMPLETED; fall back to live
  // progress numbers while the workflow is still running.
  const total = isCompleted && result ? result.total_synced : (progress?.estimated_total ?? 0);
  const processed =
    isCompleted && result ? result.ch_rows_written : (progress?.products_processed ?? 0);
  const percent = progress?.percent_complete ?? (isCompleted ? 100 : 0);

  const view: CatalogImportView = {
    workflowId: data.workflowId,
    source,
    status: mapWireStatus(data.status),
    totalProducts: total,
    importedProducts: processed,
    percentComplete: percent,
    estimatedTimeRemainingSeconds: progress?.timing?.estimated_seconds_remaining ?? null,
    // Run-controls fields (ingest-run-controls contract §1). Defaulted to null so
    // a backend that predates the contract simply hides the new controls.
    phase: mapWirePhase(data.status),
    pipelineRunId: data.pipelineRunId ?? null,
    errorCode: data.errorCode ?? null,
    retryable: data.retryable ?? null,
    userMessage: data.userMessage ?? null,
    failedPage: data.failedPage ?? null,
    pagesCompleted: progress?.pages_completed ?? null,
    stages: data.stages ?? [],
  };

  if (isCompleted) {
    const completionSeconds =
      elapsedSecondsFromStages(data.stages) ?? progress?.timing?.elapsed_seconds ?? null;
    if (completionSeconds != null) {
      view.completionTimeFormatted = formatElapsed(completionSeconds);
    }
  }
  if ((data.status === 'FAILED' || data.status === 'CANCELLED') && data.error) {
    view.failureReason = data.error;
  }
  return view;
}

/** Pipeline elapsed time = last stage's `completedAt` − first stage's
 *  `startedAt`, in seconds. Returns `null` if the stages array is missing,
 *  empty, or the last stage hasn't completed (defensive — the caller falls
 *  back to `progress.timing.elapsed_seconds` in that case). */
function elapsedSecondsFromStages(stages: WorkflowStatusResponse['stages']): number | null {
  if (!stages?.length) return null;
  const first = stages[0];
  const last = stages[stages.length - 1];
  if (!first || !last?.completedAt) return null;
  const startMs = Date.parse(first.startedAt);
  const endMs = Date.parse(last.completedAt);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return null;
  return Math.max(0, (endMs - startMs) / 1000);
}
